/**
 * Composer draft persistence — keeps an in-progress note / clip
 * composer alive across a route navigation, a refresh, or an
 * explicit clip-mode exit, so a coach never loses half-written
 * feedback the way they could before (close/reopen survived
 * in-session via `composerOverride`, but a navigation, an F5, or a
 * Quick-Feedback escalation hop dropped everything typed).
 *
 * Storage: `sessionStorage` (NOT localStorage). A draft is scoped to
 * the browser TAB and the current session — it should survive a
 * refresh and an intra-app navigation, but a coach who closes the
 * tab is done with that draft. sessionStorage gives exactly that
 * lifetime; localStorage would resurrect stale drafts days later.
 *
 * Key namespace: `tktic_cr_draft:<teamId>:<kind>:<target>` where
 *   - `<kind>` is `note` or `clip`,
 *   - `<target>` is `create` for a fresh composer or `edit:<id>` for
 *     an edit-mode composer (so a draft on note 5 never bleeds into
 *     the create form or note 6).
 * The `tktic_*` prefix follows the namespace `playbackMemory.ts`
 * established (the rewrite deliberately dropped the legacy `replay_*`
 * prefix). `teamId` keys the draft to the active scope so switching
 * teams doesn't surface another team's in-progress note.
 *
 * Privacy: a draft may contain `coach_private_note` — that is fine.
 * This is the AUTHOR's own tab, the author is a coach, and the value
 * never leaves the device. The privacy ladder protects coach-only
 * fields from VIEWERS, not from the coach who is writing them.
 *
 * All storage access is wrapped in try/catch — private mode + quota
 * errors fall through to a no-op, never throwing out to React render
 * (same discipline as `playbackMemory.ts`).
 */

import type { ComposerValues } from "./components/Composer";
import type { ClipComposerValues } from "./components/ClipComposer";

const KEY_PREFIX = "tktic_cr_draft";

export type DraftKind = "note" | "clip";

/** A persisted draft is a partial of whichever composer's values —
 * partial because we snapshot live RHF state that may be mid-edit. */
export type NoteDraft = Partial<ComposerValues>;
export type ClipDraft = Partial<ClipComposerValues>;
export type ComposerDraft = NoteDraft | ClipDraft;

/** Identifies which composer draft we're addressing. `editId` is the
 * note/clip id in edit mode; omit (or pass null) for a fresh
 * create-mode composer. */
export type DraftScope = {
  teamId: string | null | undefined;
  kind: DraftKind;
  editId?: string | null;
};

function draftKey({ teamId, kind, editId }: DraftScope): string {
  const team = teamId || "noteam";
  const target = editId ? `edit:${editId}` : "create";
  return `${KEY_PREFIX}:${team}:${kind}:${target}`;
}

/** Treat an all-empty / whitespace-only draft as "no draft" — we
 * don't want an untouched composer to leave a phantom draft that
 * re-seeds an empty override and defeats the computed defaults. A
 * draft counts as meaningful if any string field has non-whitespace
 * content, any player is tagged, or a clip carries finite timing bounds.
 * Callers gate untouched forms with RHF dirty state before saving. */
function isMeaningfulDraft(draft: ComposerDraft): boolean {
  for (const [key, value] of Object.entries(draft)) {
    if (typeof value === "string" && value.trim() !== "") return true;
    if (Array.isArray(value) && value.length > 0) return true;
    if (
      (key === "start_seconds" || key === "end_seconds") &&
      typeof value === "number" &&
      Number.isFinite(value)
    ) {
      return true;
    }
  }
  return false;
}

/** Persist a draft for the given scope. A non-meaningful (empty)
 * draft clears any existing stored draft instead of writing a
 * phantom — symmetric with `loadDraft` returning null for those. */
export function saveDraft(scope: DraftScope, draft: ComposerDraft): void {
  try {
    const key = draftKey(scope);
    if (!isMeaningfulDraft(draft)) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // private mode / quota — no-op.
  }
}

/** Load a draft for the given scope, or null if none is stored or
 * the stored value is malformed. Returns the parsed partial as-is;
 * the caller merges it into the composer override. */
export function loadDraft(scope: DraftScope): ComposerDraft | null {
  try {
    const raw = sessionStorage.getItem(draftKey(scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return null;
    if (!isMeaningfulDraft(parsed as ComposerDraft)) return null;
    return parsed as ComposerDraft;
  } catch {
    return null;
  }
}

/** Remove a stored draft for the given scope. Called on a successful
 * save and on explicit clip-mode exit (treated as cancel). */
export function clearDraft(scope: DraftScope): void {
  try {
    sessionStorage.removeItem(draftKey(scope));
  } catch {
    // ignore
  }
}
